import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Grid,
    Box,
    Typography,
    Pagination,
    PaginationItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Checkbox,
    Popover,
    Skeleton,
    TableSortLabel,
} from '@mui/material';
import { CustomAccordion, CustomModal, ConfirmationModal } from '../../../shared';
import {
    ActionsIcon,
    ArrowLeftIcon,
    ArrowRightIcon,
    DownloadIcon,
    Edit,
    Trash,
} from '../../../../assets';
import {
    getAllCampaigns,
    deleteCampaign,
} from '../../../../redux/thunks/campaignThunks';
import { actions as campaignActions } from '../../../../redux/reducers/campaignReducer';
import CreateManualCampaign from '../manualCampaign';
import CreateAlgorithmCampaign from '../algorithmCampaign';
import {
    CAMPAIGN_GENERATE_TYPE_MANUAL,
    CAMPAIGN_GENERATE_TYPE_ALGORITHM,
} from '../../../../constants';
import { getSortedData } from '../../../../utility';

export const CampaignForm = (props) => {
    const { campaignId, handleClose } = props;
    const { campaignGenerationType } = useSelector((state) => state.campaign);
    const COMPONENT_MAP = {
        [CAMPAIGN_GENERATE_TYPE_MANUAL]: CreateManualCampaign,
        [CAMPAIGN_GENERATE_TYPE_ALGORITHM]: CreateAlgorithmCampaign,
    };

    const FormComponent = COMPONENT_MAP[campaignGenerationType];
    return <FormComponent campaignId={campaignId} handleClose={handleClose} />;
};

function CampaignsTable(props) {
    const {} = props;
    const dispatch = useDispatch();

    const [editCampaignId, setEditCampaignId] = useState(null);
    const [editCampaignShowModal, setEditCampaignShowModal] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [showDeleteCampaignModal, setShowDeleteCampaignModal] = useState(false);
    const open = Boolean(anchorEl);

    const { setAllCampaigns, selectCampaignRows, resetCampaignState } =
        campaignActions;
    const {
        campaigns: campaignsList,
        isLoading,
        selectAllCampaigns,
        selectedRows,
    } = useSelector((state) => state.campaign);
    const { company } = useSelector((state) => state.company);
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState(null);

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };
    const createSortHandler = (property) => () => {
        handleRequestSort(property);
    };

    const sortedData = React.useMemo(() => {
        return getSortedData(orderBy, order, campaignsList);
    }, [order, orderBy, campaignsList]); 
    const handleEditCampaignShowModal = () => {
        dispatch(resetCampaignState());
        setEditCampaignShowModal(!editCampaignShowModal);
    };

    const handleCheckboxChange = (id) => {
        let index = selectedRows.findIndex((campaignId) => campaignId === id);
        let updatedRows = [];

        if (index > -1) {
            updatedRows = selectedRows.filter(
                (selectedId) => selectedId !== id
            );
        } else {
            updatedRows = [...selectedRows, id];
        }
        let allIdsPresent = campaignsList.every((campaign) =>
            updatedRows.includes(campaign.id)
        );

        dispatch(setAllCampaigns({ isSelected: allIdsPresent }));
        dispatch(selectCampaignRows({ rows: updatedRows }));
    };

    const handleSelectAll = () => {
        if (selectAllCampaigns) {
            dispatch(selectCampaignRows({ rows: [] }));
        } else {
            const allCampaignIds = campaignsList?.map((campaign) => campaign.id);
            dispatch(selectCampaignRows({ rows: allCampaignIds }));
        }
        dispatch(setAllCampaigns({ isSelected: !selectAllCampaigns }));
    };

    const handlePopoverOpen = (event, editId) => {
        event.preventDefault();
        setAnchorEl(event.currentTarget);
        setEditCampaignId(editId);
    };

    const handlePopoverClose = () => {
        setAnchorEl(null);
    };

    const handleCampaignRowClick = (event, editId) =>{
        const isCheckboxColumn = event.target.closest('.table-row-checkbox');
        const isActionsColumn = event.target.closest('.table-row-actions');
        const isPopoverOpen = Boolean(anchorEl);
        if (!isCheckboxColumn && !isActionsColumn && !isPopoverOpen) {
            setEditCampaignId(editId);
            handleEditCampaign();
            handlePopoverClose();
        }
    };

    const handleEditCampaign = () => {
        handlePopoverClose();
        handleEditCampaignShowModal();
    };

    const handleDeleteCampaignModal = () => {
        handlePopoverClose();
        setShowDeleteCampaignModal(!showDeleteCampaignModal);
    };

    const handleCampaignDelete = () => {
        handlePopoverClose();
        if(editCampaignId){
            dispatch(
                deleteCampaign({ companyId: company.id, campaignId: editCampaignId })
            ).then((result)=> {
                handleDeleteCampaignModal();
                dispatch(getAllCampaigns({ companyId: company.id }));
            }).catch(error => console.error("Error while deleting campaign", error));
        }
    };

    const renderNoTableData = () => {
        return (
            <TableRow className='height-280px'>
                <TableCell colSpan={5}>
                    <Box textAlign="center" p={3}>
                        <Typography className="text-sm-regular gray-900">
                            No Campaigns data to display.
                        </Typography>
                    </Box>
                </TableCell>
            </TableRow>
        );
    };

    const renderTableData = () => {
        return campaignsList && campaignsList.length > 0
            ? sortedData?.map((campaign, index) => {
                let campaignId = campaign.id;
                return (
                    <>
                        <TableRow key={'campaign' + campaign.id} onClick={(event)=> handleCampaignRowClick(event, campaign.id)} className="icon">
                            <TableCell className="table-row-data no-border table-row-checkbox">
                                <Checkbox
                                    checked={selectedRows.includes(campaign.id)}
                                    onChange={() =>
                                        handleCheckboxChange(campaign.id)
                                    }
                                />
                            </TableCell>
                            <TableCell className="table-row-data no-border">
                                {campaign.id}
                            </TableCell>
                            <TableCell className="table-row-data no-border">
                                {campaign.name}
                            </TableCell>
                            <TableCell className="table-row-data no-border">
                                <Box className="flex-wrap-center-flex-start-gap-4px">
                                    {campaign?.triggers?.map((query, index) => {
                                        return (
                                            <Box
                                                key={'campaign-query' + index}
                                                className="padding-tb-2-rl-8 br-20px bg-primary-500"
                                            >
                                                <Typography className="text-xs-medium white">
                                                    {query?.name}
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </TableCell>
                            <TableCell className="table-row-data no-border icon table-row-actions">
                                <img
                                    src={ActionsIcon}
                                    height={20}
                                    width={20}
                                    onClick={(event) => handlePopoverOpen(event, campaign.id)}
                                />
                                <Popover
                                    open={open}
                                    anchorEl={anchorEl}
                                    onClose={handlePopoverClose}
                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'right',
                                    }}
                                    transformOrigin={{
                                        vertical: 'top',
                                        horizontal: 'right',
                                    }}
                                    disableScrollLock={true}
                                    sx={{
                                        '& .MuiPaper-root': {
                                            boxShadow:
                                                '0px 4px 6px -2px rgba(16, 24, 40, 0.05), inset 0px 8px 10px -7px rgba(16, 24, 40, 0.10)',
                                        },
                                    }}
                                >
                                    <Box>
                                        <Box
                                            className="flex-row gap-12 padding-tb-10-rl-16 icon table-row-actions"
                                            onClick={handleEditCampaign}
                                        >
                                            <Edit color="#667085" />
                                            <Typography className="text-sm-regular gray-700">
                                                Edit
                                            </Typography>
                                        </Box>
                                        <Box
                                            className="flex-row gap-12 padding-tb-10-rl-16 icon table-row-actions"
                                            onClick={handleDeleteCampaignModal}
                                        >
                                            <Trash color="#667085" />
                                            <Typography className="text-sm-regular gray-700">
                                                Delete
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Popover>
                            </TableCell>
                        </TableRow>
                    </>)
                })
            : renderNoTableData();
    };

    const campaignsTableComponent = () => (
        <TableContainer key='campaign-table-container' component={Paper} className="no-box-shadow no-border">
            <Table key='campaign-table' className="table-border-collapse no-border">
                <TableHead className="queries-table-heading">
                    <TableRow>
                        <TableCell className="queries-headers-title">
                            <Checkbox
                                checked={selectAllCampaigns}
                                onChange={handleSelectAll}
                            />
                        </TableCell>
                        <TableCell className="queries-headers-title">
                            <TableSortLabel active={orderBy === "id"}
                                direction={orderBy === "id" ? order : 'asc'}
                                onClick={createSortHandler("id")}>
                                ID
                            </TableSortLabel>
                        </TableCell>
                        <TableCell className="queries-headers-title">
                            <TableSortLabel active={orderBy === "name"}
                                direction={orderBy === "name" ? order : 'asc'}
                                onClick={createSortHandler("name")}>
                                Name
                            </TableSortLabel>
                        </TableCell>
                        <TableCell className="queries-headers-title">
                            Queries
                        </TableCell>
                        <TableCell className="queries-headers-title"></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody key='campaign-table-body'>
                    {isLoading
                        ? Array.from({ length: 10 })?.map((_, index) => (
                              <TableRow key={'campaign-loading-row-' + index}>
                                  {Array.from({ length: 5 })?.map((_, index) => (
                                      <TableCell
                                          className="table-row-data no-border"
                                          key={'campaigns_skeleton_' + index}
                                      >
                                          <Skeleton animation="wave" />
                                      </TableCell>
                                  ))}
                              </TableRow>
                          ))
                        : renderTableData()}
                </TableBody>
            </Table>
        </TableContainer>
    );

    const campaigns = () => {
        return (
            <Grid container spacing={'24px'} className="flex-row">
                <Grid item xs={12}>
                    <Box>{campaignsTableComponent()}</Box>
                </Grid>
            </Grid>
        );
    };

    return (
        <Box className="box-sizing-border-box border-1-gray br-8px">
            <CustomAccordion
                title="See Full List Below"
                component={campaigns()}
                accordionContainerBackground="bg-primary-50"
                accordionTitleColor="primary-color"
                acoordianDetailsBackground="bg-gray-50"
                byDefaultOpen={true}
            />
            <CustomModal
                modalOpen={editCampaignShowModal}
                component={
                    <CampaignForm
                        campaignId={editCampaignId}
                        handleClose={handleEditCampaignShowModal}
                    />
                }
                onClose={handleEditCampaignShowModal}
            />
            <ConfirmationModal
                open={showDeleteCampaignModal}
                onClose={handleDeleteCampaignModal}
                content={
                    <Box>
                        <Typography className="mb-20px mt-20px" variant="body1">
                            Are you sure you want to delete this campaign?
                        </Typography>
                        <Box className="mb-20px mt-20px">
                            <Typography variant="body2" color="error">
                                Warning: This action cannot be undone!
                            </Typography>
                        </Box>
                    </Box>
                }
                title={
                    <Typography className="queries-table-title">
                        Delete Campaign
                    </Typography>
                }
                primaryButtonText={'Confirm'}
                onConfirm={handleCampaignDelete}
            />
        </Box>
    );
}

export default CampaignsTable;
